import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '../context/AuthContext'
import LanguageSwitcher from '../components/LanguageSwitcher'
import DarkModeToggle from '../components/DarkModeToggle'
import SkipNavigation from '../components/SkipNavigation'
import SEO from '../components/SEO'

function Home() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { user, signOut } = useAuth()
  const [joinCode, setJoinCode] = useState('')
  const [playerName, setPlayerName] = useState('')
  const [loggingOut, setLoggingOut] = useState(false)

  // Generate WebApplication Schema.org structured data
  const webAppSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebApplication',
    name: t('home.title'),
    description: t('home.description'),
    url: window.location.origin,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'EUR',
    },
    featureList: [
      t('home.feature1'),
      t('home.feature2'),
      t('home.feature3'),
      t('home.feature4'),
    ],
    screenshot: `${window.location.origin}/og-image.png`,
    softwareVersion: '2.0.0',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.8',
      ratingCount: '100',
    },
  }

  const handleJoin = (e) => {
    e.preventDefault()
    if (!joinCode.trim()) {
      alert(t('home.errors.noCode'))
      return
    }
    if (!playerName.trim()) {
      alert(t('home.errors.noName'))
      return
    }
    navigate(`/player?code=${joinCode}&name=${encodeURIComponent(playerName)}`)
  }

  const handleLogout = async () => {
    try {
      setLoggingOut(true)
      await signOut()
    } catch (error) {
      console.error('Logout error:', error)
      alert('Failed to logout. Please try again.')
    } finally {
      setLoggingOut(false)
    }
  }

  return (
    <>
      <SEO page="home" />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webAppSchema)}
        </script>
      </Helmet>
      <SkipNavigation />
      <div className="min-h-screen flex items-center justify-center p-4">
        {/* Controls - Top Right */}
      <div className="fixed top-4 right-4 z-50 flex items-center gap-2" role="navigation" aria-label={t('accessibility.utilities', 'Utilities')}>
        <DarkModeToggle />
        <LanguageSwitcher />
        {user && (
          <div className="flex items-center gap-2 bg-white dark:bg-gray-800 rounded-lg px-3 py-2 shadow-md">
            {user.user_metadata?.avatar_url && (
              <img
                src={user.user_metadata.avatar_url}
                alt={user.user_metadata?.full_name || user.email}
                className="w-6 h-6 rounded-full"
              />
            )}
            <span className="text-sm text-gray-700 dark:text-gray-300 max-w-[120px] truncate">
              {user.user_metadata?.full_name || user.email}
            </span>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="ml-2 text-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 font-semibold disabled:opacity-50"
              aria-label={t('common.logout')}
            >
              {t('common.logout')}
            </button>
          </div>
        )}
      </div>

      <div className="w-full max-w-2xl">
        <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-8 md:p-12 transition-colors" id="main-content">
          {/* Header */}
          <div className="text-center mb-10">
            <h1 className="text-5xl font-bold mb-3 dark:text-white">
              <span aria-hidden="true">🃏 </span>
              {t('home.title')}
            </h1>
            <p className="text-gray-600 dark:text-gray-300 text-lg">
              {t('home.subtitle')}
            </p>
          </div>

          {/* Buttons */}
          <div className="space-y-6">
            {/* Moderator Button */}
            <button
              onClick={() => navigate('/moderator')}
              className="w-full bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-500 dark:hover:bg-indigo-600 text-white rounded-xl p-6 transition-all transform hover:scale-105 shadow-lg"
              aria-label={t('home.startSession') + ' - ' + t('home.asModerator')}
            >
              <div className="flex items-center justify-center gap-4">
                <span className="text-4xl" aria-hidden="true">👑</span>
                <div className="text-left">
                  <div className="text-xl font-bold">{t('home.startSession')}</div>
                  <div className="text-sm opacity-90">{t('home.asModerator')}</div>
                </div>
              </div>
            </button>

            {/* Divider */}
            <div className="flex items-center gap-4">
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
              <span className="text-gray-500 dark:text-gray-400 font-semibold">{t('home.or')}</span>
              <div className="flex-1 h-px bg-gray-300 dark:bg-gray-600"></div>
            </div>

            {/* Join Form */}
            <form onSubmit={handleJoin} className="space-y-4" aria-label={t('accessibility.joinSessionForm', 'Join session form')}>
              <div>
                <label htmlFor="session-code" className="sr-only">
                  {t('home.enterCode')}
                </label>
                <input
                  id="session-code"
                  type="text"
                  placeholder={t('home.enterCode')}
                  maxLength={6}
                  value={joinCode}
                  onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none text-lg bg-white dark:bg-gray-700 dark:text-white transition-colors"
                  aria-required="true"
                />
              </div>
              <div>
                <label htmlFor="player-name" className="sr-only">
                  {t('home.yourName')}
                </label>
                <input
                  id="player-name"
                  type="text"
                  placeholder={t('home.yourName')}
                  value={playerName}
                  onChange={(e) => setPlayerName(e.target.value)}
                  className="w-full px-4 py-3 border-2 border-gray-300 dark:border-gray-600 rounded-lg focus:border-indigo-500 dark:focus:border-indigo-400 focus:outline-none text-lg bg-white dark:bg-gray-700 dark:text-white transition-colors"
                  aria-required="true"
                />
              </div>
              <button
                type="submit"
                className="w-full bg-cyan-600 hover:bg-cyan-700 dark:bg-cyan-500 dark:hover:bg-cyan-600 text-white rounded-xl p-4 transition-all transform hover:scale-105 shadow-lg font-semibold text-lg"
                aria-label={t('home.join') + ' - ' + t('accessibility.joinAsPlayer', 'Join as player')}
              >
                <div className="flex items-center justify-center gap-3">
                  <span className="text-2xl" aria-hidden="true">🎮</span>
                  <span>{t('home.join')}</span>
                </div>
              </button>
            </form>
          </div>

          {/* Info Section */}
          <div className="mt-10 pt-8 border-t border-gray-200 dark:border-gray-700">
            <h3 className="font-bold text-lg mb-3 text-indigo-600 dark:text-indigo-400">
              {t('home.whatIs')}
            </h3>
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              {t('home.description')}
            </p>
            <ul className="space-y-2 text-gray-600 dark:text-gray-300">
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 dark:text-indigo-400">•</span>
                <span>{t('home.feature1')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 dark:text-indigo-400">•</span>
                <span>{t('home.feature2')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 dark:text-indigo-400">•</span>
                <span>{t('home.feature3')}</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-indigo-600 dark:text-indigo-400">•</span>
                <span>{t('home.feature4')}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    </>
  )
}

export default Home
